// The study-note reader's enhancements. Everything here is optional: with no
// script the page reads, links, reveals answers (<details>) and switches to
// must-know only (a plain link). This adds:
//   1. the sections list marks where you are and what you have seen;
//   2. what you have seen is sent to the server, so the notes page can say
//      "Pick up where you stopped" (a beacon, never a GET);
//   3. the must-know switch flips without a reload, remembered per device;
//   4. the A- / A+ control, sharing /learn's remembered size (text_scale.js);
//   5. the right rail (study_notes/_companion) shows the panel for the section
//      on screen, and counts a section the first time it is seen.
import { STEPS, read as readScale, write as writeScale, stepFrom } from "./text_scale";

const FOCUS_KEY = "pq:study-note-focus";
const INTRO_KEY = "pq:note-explain-introduced";
const INTRO_MS = 3000;

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

const store = {
  get() { try { return window.localStorage.getItem(FOCUS_KEY); } catch { return null; } },
  set(value) { try { window.localStorage.setItem(FOCUS_KEY, value); } catch { /* storage refused */ } },
};

function setupReader(article) {
  const url = article.dataset.progressUrl;
  const pending = new Set();
  let timer = null;

  const flush = () => {
    if (!url || pending.size === 0) return;
    const body = new FormData();
    body.append("authenticity_token", csrfToken() || "");
    pending.forEach((anchor) => body.append("anchors[]", anchor));
    pending.clear();
    if (navigator.sendBeacon) navigator.sendBeacon(url, body);
    else fetch(url, { method: "POST", body, credentials: "same-origin", keepalive: true });
  };

  const queue = (anchor) => {
    pending.add(anchor);
    clearTimeout(timer);
    timer = setTimeout(flush, 1500);
  };

  const links = new Map(
    [...document.querySelectorAll("[data-toc-link]")].map((a) => [a.dataset.tocLink, a])
  );

  const companion = setupCompanion(document.querySelector("[data-companion]"));

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      // A section folded away by "My outline only" is not being read.
      if (article.dataset.outlineFocus === "on" && !entry.target.classList.contains("study-note__section--on-outline")) return;
      const id = entry.target.id;
      links.forEach((a) => a.classList.remove("is-active"));
      links.get(id)?.classList.add("is-active", "is-seen");
      companion.show(id);
      companion.seen(id);
      queue(id);
    });
  }, { rootMargin: "0px 0px -60% 0px" });

  const sections = [...article.querySelectorAll("[data-note-section]")];
  sections.forEach((section) => observer.observe(section));

  // The -60% top band never admits a short final section at maximum scroll —
  // there is no more page below it to push it up there. The footer, observed
  // against the full viewport (the default root), stands in for "reached the
  // end" and marks the LAST section seen when it comes into view.
  const foot = article.querySelector("[data-note-end]");
  const lastSection = sections[sections.length - 1];
  if (foot && lastSection) {
    const footObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (article.dataset.outlineFocus === "on" && !lastSection.classList.contains("study-note__section--on-outline")) return;
        const id = lastSection.id;
        links.get(id)?.classList.add("is-seen");
        companion.seen(id);
        queue(id);
      });
    });
    footObserver.observe(foot);
  }

  document.addEventListener("visibilitychange", () => { if (document.hidden) flush(); });
  window.addEventListener("pagehide", flush);

  const toggle = article.querySelector("[data-focus-toggle]");
  const apply = (focus) => {
    article.dataset.focus = focus;
    if (toggle) toggle.textContent = focus === "must_know" ? "Show everything" : "Must-know only";
  };
  if (!new URLSearchParams(window.location.search).has("focus") && store.get() === "must_know") apply("must_know");
  toggle?.addEventListener("click", (event) => {
    event.preventDefault();
    const next = article.dataset.focus === "must_know" ? "all" : "must_know";
    apply(next);
    store.set(next);
  });

  // "My outline only" (a book chapter with a section on the outline): the
  // same switch, remembered per book under the key the page names. An
  // explicit ?focus in the address wins over the remembered one. An article
  // without data-outline-focus never takes a stored "on": it would fold whole.
  const outlineToggle = document.querySelector("[data-outline-toggle]");
  if (outlineToggle && article.dataset.outlineFocus && outlineToggle.dataset.outlineKey) {
    const OUTLINE_KEY = outlineToggle.dataset.outlineKey;
    const applyOutline = (on) => {
      article.dataset.outlineFocus = on ? "on" : "off";
      outlineToggle.textContent = on ? outlineToggle.dataset.labelOn : outlineToggle.dataset.labelOff;
    };
    let remembered = null;
    try { remembered = window.localStorage.getItem(OUTLINE_KEY); } catch { /* storage refused */ }
    if (!new URLSearchParams(window.location.search).has("focus") && remembered) applyOutline(remembered === "on");
    outlineToggle.addEventListener("click", (event) => {
      event.preventDefault();
      const on = article.dataset.outlineFocus !== "on";
      applyOutline(on);
      try { window.localStorage.setItem(OUTLINE_KEY, on ? "on" : "off"); } catch { /* storage refused */ }
    });
  }
}

// The rail's panels ship hidden; exactly one is shown, the section on screen's.
// "Read N" starts from what the server had recorded, so a section already read
// on an earlier visit is not counted twice.
function setupCompanion(rail) {
  if (!rail) return { show() {}, seen() {} };

  const panels = new Map(
    [...rail.querySelectorAll("[data-companion-for]")].map((p) => [p.dataset.companionFor, p])
  );
  const count = rail.querySelector("[data-companion-read]");
  let seen;
  try { seen = new Set(JSON.parse(rail.dataset.companionSeen || "[]")); } catch { seen = new Set(); }

  // At the top of the page no section has reached the observer's band yet;
  // the first one is what the reader is about to read.
  const first = panels.values().next().value;
  if (first) first.hidden = false;

  return {
    show(id) {
      if (!panels.has(id)) return;
      panels.forEach((panel, anchor) => { panel.hidden = anchor !== id; });
    },
    seen(id) {
      if (!panels.has(id) || seen.has(id)) return;
      seen.add(id);
      if (count) count.textContent = String(seen.size);
    },
  };
}

// The size is set as --type-scale on the page, which every reading size under
// .study-note-page derives from (components/_study_notes.scss). The control
// itself sits in the top bar, outside it, so it never walks out from under
// the cursor.
function setupTextSize(page, control) {
  const value = control.querySelector("[data-text-size-value]");
  const shrink = control.querySelector('[data-text-size-step="-1"]');
  const grow = control.querySelector('[data-text-size-step="1"]');
  let scale = readScale();

  const apply = () => {
    page.style.setProperty("--type-scale", String(scale));
    value.textContent = `${Math.round(scale * 100)}%`;
    shrink.disabled = STEPS.indexOf(scale) === 0;
    grow.disabled = STEPS.indexOf(scale) === STEPS.length - 1;
  };

  control.querySelectorAll("[data-text-size-step]").forEach((button) => {
    button.addEventListener("click", () => {
      scale = stepFrom(scale, Number(button.dataset.textSizeStep));
      apply();
      writeScale(scale);
    });
  });

  apply();
  control.hidden = false;
}

// "Explain this": the server ships each block's button hidden, so a page with
// no pack offers nothing it cannot answer. Here they are revealed, and a press
// (or the margin dot) is handed to the NoteExplainer island as an event — the
// reader never imports React. Hover and focus show the button on a pointer
// device; a tap on the block shows it on touch. Never hover-only: the button
// is a real, focusable control either way.
function setupExplain() {
  const blocks = document.querySelectorAll("[data-note-block]");
  if (!blocks.length) return;

  // The resting "Opens free" tag is drawn by CSS under this class, so a page
  // whose pack never ran shows no promise it cannot keep.
  document.querySelector("article.study-note")?.classList.add("note-explain-ready");

  const clearExplaining = () =>
    document.querySelectorAll(".note-block.is-explaining").forEach((el) => el.classList.remove("is-explaining"));

  blocks.forEach((block) => {
    const button = block.querySelector("[data-note-explain]");
    if (button) button.hidden = false;

    const send = (reopen) => {
      clearExplaining();
      block.classList.add("is-explaining");
      document.dispatchEvent(new CustomEvent("note:explain", {
        detail: { ref: block.dataset.noteBlock, digest: block.dataset.blockDigest, reopen },
      }));
    };

    button?.addEventListener("click", () => send(false));
    const dot = block.querySelector("[data-note-reopen]");
    if (dot) {
      dot.hidden = false;
      dot.addEventListener("click", () => send(true));
    }
    block.addEventListener("click", (event) => {
      if (event.target.closest("a, button, summary, input")) return;
      blocks.forEach((other) => other !== block && other.classList.remove("is-touched"));
      block.classList.add("is-touched");
    });
  });

  document.addEventListener("note:explain:closed", clearExplaining);
  introduceExplain([...blocks]);
}

// A student who never points at a block would never see its button, so once
// per device the first explainable block to reach the reading band lights up
// with its button for a few seconds (.is-introduced, the hover look), then
// fades. Remembered only once shown; with storage refused there is no cue,
// rather than one on every visit.
function introduceExplain(blocks) {
  try { if (window.localStorage.getItem(INTRO_KEY)) return; } catch { return; }

  const observer = new IntersectionObserver((entries) => {
    const entry = entries.find((e) => e.isIntersecting && !e.target.classList.contains("is-explaining"));
    if (!entry) return;
    observer.disconnect();
    const block = entry.target;
    block.classList.add("is-introduced");
    setTimeout(() => block.classList.remove("is-introduced"), INTRO_MS);
    try { window.localStorage.setItem(INTRO_KEY, "1"); } catch { /* storage refused */ }
  }, { rootMargin: "0px 0px -60% 0px" });

  blocks.forEach((block) => observer.observe(block));
}

function boot() {
  document.querySelectorAll("article.study-note[data-progress-url]").forEach(setupReader);

  const page = document.querySelector(".study-note-page");
  const control = document.querySelector("[data-text-size]");
  if (page && control) setupTextSize(page, control);

  setupExplain();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
